import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useCourses } from '../../hooks/use-courses';
import { useChatbot } from '../../contexts/chatbot-context';
import { useSidebar } from '../../contexts/sidebar-context';
import { ReactComponent as LocationIcon } from '../../assets/location.svg';
import { ReactComponent as LocationWhiteIcon } from '../../assets/locationwhite.svg';
import { ReactComponent as PencilIcon } from '../../assets/pencil.svg';
import { ReactComponent as TrashIcon } from '../../assets/trashbucket.svg';
import { ReactComponent as InfoIcon } from '../../assets/info.svg';
import clearLogo from '../../assets/clearlogo.svg';
import chevronDown from '../../assets/chewron_down.svg';
import CalendarSync from '../../components/calendar-sync';
import { ReactComponent as SyncIcon } from "../../assets/syncicon.svg";
import { ReactComponent as GoogleIcon } from "../../assets/blackgcalicon.svg";
import { ReactComponent as OutlookIcon } from "../../assets/blackoutlookicon.svg";
import s from './styles.module.scss';

// Hamburger menu icon component
const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Cache keys and duration
const CACHE_KEY = 'ocean_calendar_events';
const CACHE_RANGES_KEY = 'ocean_calendar_ranges';
const CACHE_TIMESTAMP_KEY = 'ocean_calendar_timestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes - refresh in background after this
const CACHE_LAST_SYNC_KEY = 'ocean_calendar_last_sync';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const SUGGESTED_EVENTS_CACHE_KEY = 'ocean_suggested_events';
const SUGGESTIONS_ENABLED_KEY = 'ocean_suggestions_enabled';

const OceanCalendar = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  const { courses: localCourses } = useCourses();
  const { isExpanded: isChatbotExpanded, openChatbot } = useChatbot();
  const [courses, setCourses] = useState([]);
  const [showNavDropdown, setShowNavDropdown] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0, position: 'right' });
  const [fetchedRanges, setFetchedRanges] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [deletedEvent, setDeletedEvent] = useState(null);
  const [showUndoToast, setShowUndoToast] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [suggestedEvents, setSuggestedEvents] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    const saved = localStorage.getItem('ocean_calendar_timezone');
    return saved || Intl.DateTimeFormat().resolvedOptions().timeZone;
  });
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [tzPortalContainers, setTzPortalContainers] = useState([]);
  const [tzDropdownFixedPos, setTzDropdownFixedPos] = useState({ top: 0, left: 0 });
  const tzDropdownPortalRef = useRef(null);
  const [mobileViewDropdownOpen, setMobileViewDropdownOpen] = useState(false);
  const [currentView, setCurrentView] = useState('timeGridWeek');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [providerStatus, setProviderStatus] = useState({});
  const [isSyncingAction, setIsSyncingAction] = useState(false);
  const undoTimeoutRef = useRef(null);
  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const modalRef = useRef(null);
  const createModalRef = useRef(null);
  const navDropdownRef = useRef(null);
  const timezoneDropdownRef = useRef(null);
  const mobileViewDropdownRef = useRef(null);
  const lastKnownSyncRef = useRef(null);
  const { isManuallyCollapsed, toggleSidebar, isCollapsed } = useSidebar();

  // Common timezones for the dropdown
  const TIMEZONE_OPTIONS = [
    { value: 'America/New_York', label: 'Eastern', abbr: 'ET' },
    { value: 'America/Chicago', label: 'Central', abbr: 'CT' },
    { value: 'America/Denver', label: 'Mountain', abbr: 'MT' },
    { value: 'America/Los_Angeles', label: 'Pacific', abbr: 'PT' },
    { value: 'America/Anchorage', label: 'Alaska', abbr: 'AKT' },
    { value: 'Pacific/Honolulu', label: 'Hawaii', abbr: 'HT' },
    { value: 'Europe/London', label: 'London', abbr: 'GMT' },
    { value: 'Europe/Paris', label: 'Paris', abbr: 'CET' },
    { value: 'Asia/Tokyo', label: 'Tokyo', abbr: 'JST' },
    { value: 'Asia/Shanghai', label: 'Shanghai', abbr: 'CST' },
    { value: 'Australia/Sydney', label: 'Sydney', abbr: 'AEST' },
  ];

  // Get timezone abbreviation for display
  const getTimezoneAbbr = (tz) => {
    const option = TIMEZONE_OPTIONS.find(o => o.value === tz);
    if (option) return option.abbr;
    try {
      return new Date().toLocaleTimeString('en-US', {
        timeZone: tz,
        timeZoneName: 'short'
      }).split(' ').pop();
    } catch {
      return 'TZ';
    }
  };

  // Handle timezone change - save to backend for worker access
  const handleTimezoneChange = async (tz) => {
    setSelectedTimezone(tz);
    localStorage.setItem('ocean_calendar_timezone', tz);
    setShowTimezoneDropdown(false);

    // Save to backend (for suggestion generation worker)
    try {
      const token = await getToken();
      await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/settings/timezone`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ timezone: tz })
        }
      );
    } catch (error) {
      console.error('Error saving timezone to backend:', error);
    }
  };

  // Load timezone from backend on mount (in case set on different device)
  useEffect(() => {
    const loadTimezoneFromBackend = async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/settings/timezone`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.data?.timezone) {
            setSelectedTimezone(data.data.timezone);
            localStorage.setItem('ocean_calendar_timezone', data.data.timezone);
          }
        }
      } catch (error) {
        console.error('Error loading timezone from backend:', error);
      }
    };

    loadTimezoneFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In list view, physically inject a timezone button into EVERY
  // .fc-list-day-cushion element so whichever day's header is currently
  // sticky always shows the TZ button (and it rubber-bands with that row).
  useEffect(() => {
    if (currentView !== 'listWeek') {
      setTzPortalContainers([]);
      return;
    }

    const container = calendarContainerRef.current;
    if (!container) return;

    const inject = () => {
      const cushions = container.querySelectorAll('.fc-list-day-cushion');
      if (cushions.length === 0) return;

      const next = [];
      cushions.forEach(cushion => {
        let div = cushion.querySelector('.ocean-tz-portal');
        if (!div) {
          div = document.createElement('div');
          div.className = 'ocean-tz-portal';
          // Visibility is controlled by CSS — only the topmost-sticky cushion shows it
          div.style.cssText = 'align-items:center;';
          const sideText = cushion.querySelector('.fc-list-day-side-text');
          if (sideText) {
            cushion.insertBefore(div, sideText);
          } else {
            cushion.appendChild(div);
          }
        }
        next.push(div);
      });

      // Only trigger a re-render if the set of containers actually changed
      setTzPortalContainers(prev => {
        if (prev.length !== next.length) return next;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i] !== next[i]) return next;
        }
        return prev;
      });
    };

    // Try immediately — FC's Preact render is synchronous so cushions are
    // usually already in the DOM by the time this effect fires.
    inject();
    // Also queue a RAF in case FC does a second Preact pass (e.g. resetOptions
    // in componentDidUpdate) that replaces the cushion nodes mid-frame.
    requestAnimationFrame(inject);

    // Re-inject whenever FC re-renders the list (week navigation, etc.)
    const observer = new MutationObserver(() => requestAnimationFrame(inject));
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      setTzPortalContainers([]);
    };
  }, [currentView]);

  // Track which day-cushion is currently the topmost sticky one so we can
  // show the TZ button only there (every cushion has a portal target, but
  // CSS hides them unless their cushion has the .ocean-tz-topmost class).
  useEffect(() => {
    if (currentView !== 'listWeek' || tzPortalContainers.length === 0) return;

    const container = calendarContainerRef.current;
    if (!container) return;

    const scroller = container.querySelector('.fc-scroller');
    if (!scroller) return;

    const updateTopmost = () => {
      const cushions = container.querySelectorAll('.fc-list-day-cushion');
      if (cushions.length === 0) return;

      const scrollerTop = scroller.getBoundingClientRect().top;
      let topmost = cushions[0]; // default before any cushion has stuck

      cushions.forEach(cushion => {
        // A "stuck" cushion sits at (or above) the scroller's top edge
        if (cushion.getBoundingClientRect().top <= scrollerTop + 2) {
          topmost = cushion;
        }
      });

      cushions.forEach(c => {
        c.classList.toggle('ocean-tz-topmost', c === topmost);
      });
    };

    updateTopmost();
    scroller.addEventListener('scroll', updateTopmost, { passive: true });
    return () => scroller.removeEventListener('scroll', updateTopmost);
  }, [currentView, tzPortalContainers]);

  // Load courses - use localStorage first, fallback to API
  useEffect(() => {
    const loadCourses = async () => {
      // If localStorage has courses, use them
      if (localCourses && localCourses.length > 0) {
        setCourses(localCourses);
        return;
      }

      // Fallback: fetch from API
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/courses`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.data) {
            setCourses(data.data);
            // Save to localStorage for future use
            localStorage.setItem("courses", JSON.stringify(data.data));
          }
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };

    loadCourses();
  }, [localCourses, getToken]);

  // Track window width for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for cache updates from chatbot and update React state
  useEffect(() => {
    const handleCacheUpdate = (e) => {
      const { mutations } = e.detail;
      console.log('[Calendar] Received cache update event:', mutations);
      if (!mutations || mutations.length === 0) return;

      setEvents(prevEvents => {
        let updatedEvents = [...prevEvents];

        mutations.forEach(mutation => {
          if (mutation.type === 'delete') {
            const deleteId = `stored_${mutation.event_id}`;
            updatedEvents = updatedEvents.filter(evt => evt.id !== deleteId);
          } else if (mutation.type === 'create' && mutation.event) {
            updatedEvents.push(mutation.event);
          } else if (mutation.type === 'update' && mutation.event) {
            updatedEvents = updatedEvents.map(evt =>
              evt.id === mutation.event.id ? mutation.event : evt
            );
          }
        });

        return updatedEvents;
      });
    };

    window.addEventListener('calendarCacheUpdated', handleCacheUpdate);
    return () => window.removeEventListener('calendarCacheUpdated', handleCacheUpdate);
  }, []);

  // Fetch provider status on mount
  useEffect(() => {
    // Try to load from localStorage first for instant UI feedback
    const cachedStatus = localStorage.getItem('ocean_calendar_sync_status');
    if (cachedStatus) {
      try {
        const parsed = JSON.parse(cachedStatus);
        setProviderStatus(parsed);
      } catch (e) {
        console.error('Failed to parse cached sync status:', e);
      }
    }

    fetchProviderStatus(); // Fetch fresh data in background
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProviderStatus = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/calendar/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "success" && data.data?.providers) {
          const statusMap = {};
          data.data.providers.forEach(p => {
            statusMap[p.id] = { connected: p.connected, syncing: p.syncing };
          });
          setProviderStatus(statusMap);
          localStorage.setItem('ocean_calendar_sync_status', JSON.stringify(statusMap));
        }
      }
    } catch (error) {
      console.error('Error fetching provider status:', error);
    }
  };

  // Poll sync-status to detect when Google/Outlook pushes new events
  useEffect(() => {
    const isSyncing = providerStatus.google?.syncing || providerStatus.outlook?.syncing;
    if (!isSyncing) {
      lastKnownSyncRef.current = null;
      return;
    }

    const checkSyncStatus = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/api/calendar/sync-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;

        const data = await res.json();
        const lastSync = data.data?.last_sync || data.last_sync;
        if (!lastSync) return;

        if (lastKnownSyncRef.current === null) {
          // First poll — load cached baseline so we can detect changes
          // that happened while the page was closed
          lastKnownSyncRef.current = localStorage.getItem(CACHE_LAST_SYNC_KEY);
        }

        if (lastSync !== lastKnownSyncRef.current) {
          lastKnownSyncRef.current = lastSync;
          // New sync detected — clear cache and silently refresh
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem(CACHE_RANGES_KEY);
          localStorage.removeItem(CACHE_TIMESTAMP_KEY);
          localStorage.removeItem(CACHE_LAST_SYNC_KEY);
          fetchEventsFromAPI(false);
        }
      } catch (error) {
        console.error('Error polling sync status:', error);
      }
    };

    // Poll immediately, then every 30 seconds
    checkSyncStatus();
    const interval = setInterval(checkSyncStatus, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerStatus.google?.syncing, providerStatus.outlook?.syncing]);

  const handleSyncClick = () => {
    // No functionality yet - just close dropdown
    setMobileViewDropdownOpen(false);
  };

  const handleProviderClick = async (providerId) => {
    const state = providerStatus[providerId];

    if (!state?.connected) {
      // Not connected - redirect to OAuth
      if (!user?.id) {
        console.error("No user ID available for OAuth");
        return;
      }
      const redirectUrl = encodeURIComponent("/calendar");
      const oauthUrl = `${API_BASE}/${providerId}/login/sync?state=${user.id}&redirect=${redirectUrl}`;
      window.location.href = oauthUrl;
      return;
    }

    if (state.syncing) {
      // Currently syncing - disconnect immediately
      await disconnectProvider(providerId);
      setMobileViewDropdownOpen(false);
      return;
    }

    // Connected but not syncing - set up sync
    await setupSync(providerId);
    setMobileViewDropdownOpen(false);
  };

  const disconnectProvider = async (providerId) => {
    setIsSyncingAction(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/calendar/disconnect/${providerId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchProviderStatus();
        // Trigger calendar refresh — clear cache then actually re-fetch so the
        // loading shimmer resolves (fetchEventsFromAPI clears `loading` in all
        // paths, including 401s). Just setting loading=true here would leave the
        // shimmer spinning forever since nothing else re-fetches after mount.
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_RANGES_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
        localStorage.removeItem(CACHE_LAST_SYNC_KEY);
        setFetchedRanges([]);
        await fetchEventsFromAPI(true);
      } else {
        const error = await res.json();
        console.error('Disconnect failed:', error.message);
      }
    } catch (error) {
      console.error('Disconnect failed:', error);
    } finally {
      setIsSyncingAction(false);
    }
  };

  const setupSync = async (providerId) => {
    setIsSyncingAction(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/calendar/setup-sync/${providerId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchProviderStatus();
        // Trigger calendar refresh — clear cache then actually re-fetch so the
        // loading shimmer resolves (fetchEventsFromAPI clears `loading` in all
        // paths, including 401s). Just setting loading=true here would leave the
        // shimmer spinning forever since nothing else re-fetches after mount.
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_RANGES_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
        localStorage.removeItem(CACHE_LAST_SYNC_KEY);
        setFetchedRanges([]);
        await fetchEventsFromAPI(true);
      } else {
        const error = await res.json();
        console.error('Setup sync failed:', error.message);
      }
    } catch (error) {
      console.error('Setup sync failed:', error);
    } finally {
      setIsSyncingAction(false);
    }
  };

  // Close event modal
  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setIsEditing(false);
    setEditForm({});
  };

  // Start editing
  const handleEdit = () => {
    setEditForm({
      title: selectedEvent.title,
      description: selectedEvent.description || '',
      location: selectedEvent.location || '',
      start: selectedEvent.start,
      end: selectedEvent.end
    });
    // Reposition modal higher for edit mode to ensure full form is visible
    setModalPosition(prev => ({
      ...prev,
      top: 150 // Fixed position from top for edit mode
    }));
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  // Save edited event
  const handleSaveEdit = async () => {
    if (!selectedEvent?.id) return;

    setIsSaving(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/calendar/events/${selectedEvent.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: editForm.title,
            description: editForm.description,
            location: editForm.location,
            start: editForm.start.toISOString(),
            end: editForm.end.toISOString()
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      // Update event in local state
      setEvents(prevEvents =>
        prevEvents.map(e =>
          e.id === selectedEvent.id
            ? {
                ...e,
                title: editForm.title,
                start: editForm.start.toISOString(),
                end: editForm.end.toISOString(),
                extendedProps: {
                  ...e.extendedProps,
                  description: editForm.description,
                  location: editForm.location
                }
              }
            : e
        )
      );

      // Update selected event for display
      setSelectedEvent(prev => ({
        ...prev,
        title: editForm.title,
        description: editForm.description,
        location: editForm.location,
        start: editForm.start,
        end: editForm.end
      }));

      // Clear cache so next load gets fresh data
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_RANGES_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      localStorage.removeItem(CACHE_LAST_SYNC_KEY);

      setIsEditing(false);
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete event
  const handleDelete = async () => {
    if (!selectedEvent?.id) return;

    // Store the event for potential undo
    const eventToDelete = selectedEvent;
    const eventInList = events.find(e => e.id === selectedEvent.id);

    // Remove event from local state immediately
    setEvents(prevEvents => prevEvents.filter(e => e.id !== selectedEvent.id));
    closeEventModal();

    // Show undo toast
    setDeletedEvent({ ...eventToDelete, fullEvent: eventInList });
    setShowUndoToast(true);

    // Clear any existing timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }

    // Set timeout to actually delete after 1.5 seconds
    undoTimeoutRef.current = setTimeout(async () => {
      setShowUndoToast(false);
      setDeletedEvent(null);

      // Actually delete from backend
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/calendar/events/${eventToDelete.id}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to delete event');
        }

        // Clear cache
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_RANGES_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
        localStorage.removeItem(CACHE_LAST_SYNC_KEY);
      } catch (error) {
        console.error('Error deleting event:', error);
        // Restore event if delete failed
        if (eventInList) {
          setEvents(prevEvents => [...prevEvents, eventInList]);
        }
      }
    }, 2500);
  };

  // Undo delete
  const handleUndoDelete = () => {
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }

    // Restore the event
    if (deletedEvent?.fullEvent) {
      setEvents(prevEvents => [...prevEvents, deletedEvent.fullEvent]);
    }

    setShowUndoToast(false);
    setDeletedEvent(null);
  };

  // Close create modal
  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({});
  };

  // Create new event
  const handleCreateEvent = async () => {
    if (!createForm.title) {
      alert('Please enter a title for the event');
      return;
    }

    setIsCreating(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/calendar/events`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: createForm.title,
            description: createForm.description || '',
            location: createForm.location || '',
            start: createForm.start.toISOString(),
            end: createForm.end.toISOString(),
            courseId: createForm.courseId || null
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create event');
      }

      const data = await response.json();
      const newEvent = data.data;

      // Add new event to local state
      if (newEvent) {
        setEvents(prevEvents => [...prevEvents, newEvent]);
      }

      // Clear cache
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_RANGES_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      localStorage.removeItem(CACHE_LAST_SYNC_KEY);

      closeCreateModal();
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  // Load cached events from localStorage
  const loadCachedEvents = () => {
    try {
      const cachedEvents = localStorage.getItem(CACHE_KEY);
      const cachedRanges = localStorage.getItem(CACHE_RANGES_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

      if (cachedEvents && cachedRanges && cachedTimestamp) {
        const parsedEvents = JSON.parse(cachedEvents);
        const parsedRanges = JSON.parse(cachedRanges).map(range => ({
          start: new Date(range.start),
          end: new Date(range.end)
        }));
        const timestamp = parseInt(cachedTimestamp, 10);
        const isStale = Date.now() - timestamp > CACHE_DURATION_MS;

        return { events: parsedEvents, ranges: parsedRanges, isStale };
      }
    } catch (error) {
      console.error('Error loading cached events:', error);
    }
    return null;
  };

  // Save events to localStorage cache
  const saveCachedEvents = (eventsToCache, rangesToCache, lastSync) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(eventsToCache));
      localStorage.setItem(CACHE_RANGES_KEY, JSON.stringify(rangesToCache));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      if (lastSync) {
        localStorage.setItem(CACHE_LAST_SYNC_KEY, lastSync);
      }
    } catch (error) {
      console.error('Error saving events to cache:', error);
    }
  };

  // Fetch initial large date range on mount
  useEffect(() => {
    loadInitialEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Force calendar resize when sidebar collapse state changes
  useEffect(() => {
    // Wait for CSS transition to complete (300ms)
    const timer = setTimeout(() => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.updateSize(); // Force update size
      }
    }, 310); // Slightly longer than the 300ms transition

    return () => clearTimeout(timer);
  }, [isCollapsed]); // Re-run whenever sidebar collapse state changes

  // Fetch suggested events and check if we need to generate new ones
  useEffect(() => {
    let pollInterval = null;
    let pollCount = 0;
    const MAX_POLLS = 10; // Poll up to 10 times (30 seconds total)

    const fetchSuggestedEventsFromAPI = async () => {
      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/suggested-events`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.data) {
            setSuggestedEvents(data.data);
            // Save to cache
            localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(data.data));
            // If we found suggestions, stop polling
            if (data.data.length > 0 && pollInterval) {
              clearInterval(pollInterval);
              pollInterval = null;
            }
          }
        }
      } catch (error) {
        console.error('Error loading suggested events:', error);
      }
    };

    const loadSuggestedEvents = async () => {
      // Check if suggestions are enabled
      const enabledStr = localStorage.getItem(SUGGESTIONS_ENABLED_KEY);
      const suggestionsEnabled = enabledStr === null ? true : JSON.parse(enabledStr);

      if (!suggestionsEnabled) {
        // Suggestions disabled - clear any cached suggestions and skip fetching
        setSuggestedEvents([]);
        return;
      }

      // Load from cache first for instant display
      const cached = localStorage.getItem(SUGGESTED_EVENTS_CACHE_KEY);
      if (cached) {
        try {
          const cachedEvents = JSON.parse(cached);
          // Filter out events from previous days (only show today and future)
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const filteredEvents = cachedEvents.filter(event => {
            const eventDate = new Date(event.start);
            eventDate.setHours(0, 0, 0, 0);
            return eventDate >= today;
          });
          setSuggestedEvents(filteredEvents);
          // Update cache if we filtered out any past events
          if (filteredEvents.length < cachedEvents.length) {
            localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(filteredEvents));
          }
        } catch (e) {
          console.error('Error parsing cached suggested events:', e);
        }
      }

      // Then refresh from API in background
      try {
        const token = await getToken();

        // Check if we need to generate new suggestions (Monday-based trigger)
        const checkResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/suggested-events/check-generate`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        let generationQueued = false;
        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          generationQueued = !!checkData?.data?.queued;
        }

        // Fetch existing suggested events
        await fetchSuggestedEventsFromAPI();

        // Poll if the worker was queued (Monday trigger or just-added course),
        // so the user sees suggestions without having to refresh.
        const justAddedCourse = sessionStorage.getItem('just_added_course');
        if (justAddedCourse) {
          sessionStorage.removeItem('just_added_course');
        }
        if (generationQueued || justAddedCourse) {
          // Poll every 3 seconds for up to 30 seconds
          pollInterval = setInterval(() => {
            pollCount++;
            if (pollCount >= MAX_POLLS) {
              clearInterval(pollInterval);
              pollInterval = null;
              return;
            }
            fetchSuggestedEventsFromAPI();
          }, 3000);
        }
      } catch (error) {
        console.error('Error loading suggested events:', error);
      }
    };

    loadSuggestedEvents();

    // Cleanup on unmount
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle accepting a suggested event
  const handleAcceptSuggestion = async (suggestionId) => {
    // Find the suggested event to transform
    const suggestedEvent = suggestedEvents.find(e => e.extendedProps?.suggestionId === suggestionId);
    if (!suggestedEvent) return;

    // Optimistic update: immediately transform to regular event
    const courseColor = suggestedEvent.extendedProps?.courseColor || '#6B7280';
    const optimisticEvent = {
      id: `stored_pending_${suggestionId}`, // Temporary ID until API confirms
      title: suggestedEvent.title,
      start: suggestedEvent.start,
      end: suggestedEvent.end,
      backgroundColor: courseColor,
      borderColor: courseColor,
      extendedProps: {
        source: 'academic',
        eventType: 'study',
        description: `Study session. ${suggestedEvent.extendedProps?.aiReasoning || ''}`,
        courseName: suggestedEvent.extendedProps?.courseName,
        courseId: null // Will be set by backend
      }
    };

    // Remove from suggested, add to regular events immediately
    const updatedSuggestions = suggestedEvents.filter(e => e.extendedProps?.suggestionId !== suggestionId);
    setSuggestedEvents(updatedSuggestions);
    localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(updatedSuggestions));
    setEvents(prev => [...prev, optimisticEvent]);
    closeEventModal();

    // Make API call in background
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/suggested-events/${suggestionId}/accept`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            start: suggestedEvent.start,
            end: suggestedEvent.end
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Update the temporary ID with the real one
        if (data.data?.id) {
          setEvents(prev => prev.map(e =>
            e.id === `stored_pending_${suggestionId}` ? { ...e, id: data.data.id } : e
          ));
          // Clear cache so next full refresh gets the real data
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem(CACHE_RANGES_KEY);
          localStorage.removeItem(CACHE_TIMESTAMP_KEY);
          localStorage.removeItem(CACHE_LAST_SYNC_KEY);
        }
      } else {
        // Rollback on failure
        setEvents(prev => prev.filter(e => e.id !== `stored_pending_${suggestionId}`));
        setSuggestedEvents(prev => {
          const restored = [...prev, suggestedEvent];
          localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(restored));
          return restored;
        });
      }
    } catch (error) {
      console.error('Error accepting suggestion:', error);
      // Rollback on error
      setEvents(prev => prev.filter(e => e.id !== `stored_pending_${suggestionId}`));
      setSuggestedEvents(prev => {
        const restored = [...prev, suggestedEvent];
        localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(restored));
        return restored;
      });
    }
  };

  // Handle dismissing a suggested event
  const handleDismissSuggestion = async (suggestionId) => {
    // Find the event before removing (for potential rollback)
    const suggestedEvent = suggestedEvents.find(e => e.extendedProps?.suggestionId === suggestionId);

    // Optimistic update: remove immediately
    const updatedSuggestions = suggestedEvents.filter(e => e.extendedProps?.suggestionId !== suggestionId);
    setSuggestedEvents(updatedSuggestions);
    localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(updatedSuggestions));
    closeEventModal();

    // Make API call in background
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/suggested-events/${suggestionId}/dismiss`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok && suggestedEvent) {
        // Rollback on failure
        setSuggestedEvents(prev => {
          const restored = [...prev, suggestedEvent];
          localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(restored));
          return restored;
        });
      }
    } catch (error) {
      console.error('Error dismissing suggestion:', error);
      // Rollback on error
      if (suggestedEvent) {
        setSuggestedEvents(prev => {
          const restored = [...prev, suggestedEvent];
          localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(restored));
          return restored;
        });
      }
    }
  };

  // Handle click outside modal to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeEventModal();
      }
      if (createModalRef.current && !createModalRef.current.contains(event.target)) {
        closeCreateModal();
      }
      if (navDropdownRef.current && !navDropdownRef.current.contains(event.target)) {
        setShowNavDropdown(false);
      }
      if (currentView === 'listWeek') {
        // In list view the TZ button is portalled into every day cushion.
        // Close only if the click is outside ALL portals and the dropdown.
        const inAnyPortal = tzPortalContainers.some(c => c && c.contains(event.target));
        const inDropdown = tzDropdownPortalRef.current && tzDropdownPortalRef.current.contains(event.target);
        if (!inAnyPortal && !inDropdown) setShowTimezoneDropdown(false);
      } else if (timezoneDropdownRef.current && !timezoneDropdownRef.current.contains(event.target)) {
        setShowTimezoneDropdown(false);
      }
      if (mobileViewDropdownRef.current && !mobileViewDropdownRef.current.contains(event.target)) {
        setMobileViewDropdownOpen(false);
      }
    };

    if (showEventModal || showCreateModal || showNavDropdown || showTimezoneDropdown || mobileViewDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEventModal, showCreateModal, showNavDropdown, showTimezoneDropdown, mobileViewDropdownOpen, currentView, tzPortalContainers]);

  const loadInitialEvents = async () => {
    // Try to load from cache first
    const cached = loadCachedEvents();

    if (cached) {
      // Show cached events immediately (no loading state)
      setEvents(cached.events);
      setFetchedRanges(cached.ranges);
      setLoading(false);

      // If cache is stale, refresh in background
      if (cached.isStale) {
        fetchEventsFromAPI(false); // false = don't show loading state
      }
      return;
    }

    // No cache, fetch from API with loading state
    await fetchEventsFromAPI(true);
  };

  const fetchEventsFromAPI = async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      }

      const token = await getToken();

      // Fetch a 3-month range: 1 month back, 2 months forward
      const today = new Date();
      const startDate = new Date(today);
      startDate.setMonth(today.getMonth() - 1);

      const endDate = new Date(today);
      endDate.setMonth(today.getMonth() + 2);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/calendar/events?start=${startDate.toISOString()}&end=${endDate.toISOString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();

      // Events already have backgroundColor and borderColor from backend
      const transformedEvents = data.data;
      const newRanges = [{ start: startDate, end: endDate }];

      setEvents(transformedEvents);
      setFetchedRanges(newRanges);

      // Save to cache (include sync timestamp so polling can detect changes)
      saveCachedEvents(transformedEvents, newRanges, lastKnownSyncRef.current);

      setLoading(false);
    } catch (error) {
      console.error('Error loading events from API:', error);
      setLoading(false);
    }
  };

  // Check if we need to fetch more events when date range changes
  const handleDatesSet = (dateInfo) => {
    const viewStart = dateInfo.start;
    const viewEnd = dateInfo.end;

    // Check if current view is outside our cached range
    const needsMoreData = !fetchedRanges.some(range =>
      viewStart >= range.start && viewEnd <= range.end
    );

    if (needsMoreData && !loading) {
      fetchMoreEvents(viewStart, viewEnd);
    }
  };

  // Handle view changes - track current view/date and close mobile dropdown
  const handleViewChange = (viewInfo) => {
    setMobileViewDropdownOpen(false);
    setCurrentView(viewInfo.view.type);
    setCurrentDate(viewInfo.view.currentStart);
  };

  const fetchMoreEvents = async (start, end) => {
    try {
      const token = await getToken();

      // Expand the range a bit to cache more than just what's visible
      const expandedStart = new Date(start);
      expandedStart.setDate(expandedStart.getDate() - 7); // Extra week before

      const expandedEnd = new Date(end);
      expandedEnd.setDate(expandedEnd.getDate() + 7); // Extra week after

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/calendar/events?start=${expandedStart.toISOString()}&end=${expandedEnd.toISOString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch more events');
      }

      const data = await response.json();

      // Events already have backgroundColor and borderColor from backend
      const transformedEvents = data.data;

      // Merge with existing events (avoid duplicates)
      setEvents(prevEvents => {
        const existingIds = new Set(prevEvents.map(e => e.id));
        const newEvents = transformedEvents.filter(e => !existingIds.has(e.id));
        return [...prevEvents, ...newEvents];
      });

      setFetchedRanges(prev => [...prev, { start: expandedStart, end: expandedEnd }]);
    } catch (error) {
      console.error('Error fetching more events:', error);
    }
  };

  // Handle event click
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const props = event.extendedProps;
    const eventEl = clickInfo.el;
    const view = clickInfo.view;

    // Calculate position based on clicked element
    const rect = eventEl.getBoundingClientRect();
    const modalWidth = 500; // Max width of modal
    const gap = 10; // Gap between event and modal
    const modalEstimatedHeight = 300; // Estimated height

    let top;
    let left;
    let positionSide;

    // Check if we're in list view
    if (view.type === 'listWeek') {
      // List view: position 10px to the right of center of the screen
      positionSide = 'center';

      const viewportWidth = window.innerWidth;
      const centerX = viewportWidth / 2;
      left = centerX + 10; // 10px to the right of center

      // Position vertically near the clicked event, but ensure it stays in viewport
      top = rect.top;

      // Ensure modal stays within viewport vertically
      const viewportHeight = window.innerHeight;
      if (top + modalEstimatedHeight > viewportHeight) {
        top = Math.max(10, viewportHeight - modalEstimatedHeight - 10);
      }

      // Ensure modal doesn't go off the right edge
      if (left + modalWidth > viewportWidth - 10) {
        left = viewportWidth - modalWidth - 10;
      }
    } else if (view.type === 'timeGridDay') {
      // Day view: position above the event, overlapping it slightly
      positionSide = 'above';

      // Position so the popover appears right on top of the event
      // Subtract a smaller amount to keep it closer to the event
      top = rect.top - 20; // Just a small offset to place it on top of the event

      // Center horizontally over the event
      left = rect.left + (rect.width / 2) - (modalWidth / 2);

      // Ensure modal stays within viewport horizontally
      const viewportWidth = window.innerWidth;
      if (left < 10) {
        left = 10;
      } else if (left + modalWidth > viewportWidth - 10) {
        left = viewportWidth - modalWidth - 10;
      }

      // Ensure modal stays within viewport vertically (not too high)
      if (top < 100) {
        // If too close to the top, position it below the event instead
        top = rect.bottom + gap;
      }
    } else {
      // Week/Month view: position to the side based on day of week
      const dayOfWeek = event.start.getDay();

      // Determine position: right for Sun-Wed (0-3), left for Thu-Sat (4-6)
      positionSide = dayOfWeek <= 3 ? 'right' : 'left';

      top = rect.top;

      if (positionSide === 'right') {
        left = rect.right + gap;
      } else {
        left = rect.left - modalWidth - gap;
      }

      // Ensure modal stays within viewport vertically
      const viewportHeight = window.innerHeight;
      if (top + modalEstimatedHeight > viewportHeight) {
        top = Math.max(10, viewportHeight - modalEstimatedHeight - 10);
      }
    }

    setModalPosition({ top, left, position: positionSide });

    setSelectedEvent({
      id: event.id,
      title: event.title,
      course: props.courseName,
      type: props.eventType,
      description: props.description,
      location: props.location,
      calendarType: props.calendarType,
      gradePercentage: props.gradePercentage,
      start: event.start,
      end: event.end,
      color: event.backgroundColor || event.borderColor || '#3B82F6',
      isSuggestion: props.source === 'suggestion',
      suggestionId: props.suggestionId,
      aiReasoning: props.aiReasoning,
      userMovedTime: props.userMovedTime
    });

    setShowEventModal(true);
  };

  // Handle single tap/click on a date/time to create event
  const handleDateClick = (dateClickInfo) => {
    const start = dateClickInfo.date;
    const end = new Date(start.getTime() + 60 * 60 * 1000); // 1 hour default

    // Set up create form with selected times
    setCreateForm({
      title: '',
      description: '',
      location: '',
      start: start,
      end: end,
      courseId: null
    });

    // Position modal in center of screen
    setModalPosition({
      top: 150,
      left: window.innerWidth / 2 - 250, // Center horizontally (modal is ~500px wide)
      position: 'center'
    });

    setShowCreateModal(true);
  };

  // Handle date/time selection (drag) for creating new events
  const handleSelect = (selectInfo) => {
    const start = selectInfo.start;
    let end = selectInfo.end;

    // If user just clicked (didn't drag), default to 1 hour duration
    const duration = (end - start) / (1000 * 60); // duration in minutes
    if (duration < 30) {
      end = new Date(start.getTime() + 60 * 60 * 1000); // 1 hour default
    }

    // Set up create form with selected times
    setCreateForm({
      title: '',
      description: '',
      location: '',
      start: start,
      end: end,
      courseId: null
    });

    // Position modal in center of screen
    setModalPosition({
      top: 150,
      left: window.innerWidth / 2 - 250, // Center horizontally (modal is ~500px wide)
      position: 'center'
    });

    setShowCreateModal(true);
    selectInfo.view.calendar.unselect();
  };

  // Handle event drag/drop
  const handleEventDrop = async (dropInfo) => {
    const event = dropInfo.event;
    const eventId = event.id;
    const isSuggestion = event.extendedProps?.source === 'suggestion';

    // Handle suggested events locally (no backend update needed yet)
    if (isSuggestion) {
      const suggestionId = event.extendedProps?.suggestionId;
      const updatedSuggestions = suggestedEvents.map(s => {
        if (s.extendedProps?.suggestionId === suggestionId) {
          return {
            ...s,
            start: event.start.toISOString(),
            end: event.end.toISOString(),
            extendedProps: {
              ...s.extendedProps,
              userMovedTime: true // Mark as user-adjusted
            }
          };
        }
        return s;
      });
      setSuggestedEvents(updatedSuggestions);
      localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(updatedSuggestions));
      return;
    }

    // Handle regular events via API
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/calendar/events/${eventId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            start: event.start.toISOString(),
            end: event.end.toISOString()
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      // Clear cache so next load gets fresh data
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_RANGES_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      localStorage.removeItem(CACHE_LAST_SYNC_KEY);

    } catch (error) {
      console.error('Error updating event:', error);
      // Revert the drag if API call fails
      dropInfo.revert();
    }
  };

  // Handle event resize (changing duration)
  const handleEventResize = async (resizeInfo) => {
    const event = resizeInfo.event;
    const eventId = event.id;
    const isSuggestion = event.extendedProps?.source === 'suggestion';

    // Handle suggested events locally (no backend update needed yet)
    if (isSuggestion) {
      const suggestionId = event.extendedProps?.suggestionId;
      const updatedSuggestions = suggestedEvents.map(s => {
        if (s.extendedProps?.suggestionId === suggestionId) {
          return {
            ...s,
            start: event.start.toISOString(),
            end: event.end.toISOString(),
            extendedProps: {
              ...s.extendedProps,
              userMovedTime: true // Mark as user-adjusted
            }
          };
        }
        return s;
      });
      setSuggestedEvents(updatedSuggestions);
      localStorage.setItem(SUGGESTED_EVENTS_CACHE_KEY, JSON.stringify(updatedSuggestions));
      return;
    }

    // Handle regular events via API
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/calendar/events/${eventId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            start: event.start.toISOString(),
            end: event.end.toISOString()
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      // Clear cache
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_RANGES_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      localStorage.removeItem(CACHE_LAST_SYNC_KEY);

    } catch (error) {
      console.error('Error updating event:', error);
      // Revert the resize if API call fails
      resizeInfo.revert();
    }
  };

  // Handle task status toggle (completion checkbox)
  const handleTaskStatusToggle = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'open' : 'done';
    const originalEvents = [...events];

    // 1. Calculate new state
    const updatedEvents = events.map(e => {
      if (e.extendedProps?.taskId === taskId) {
        return {
          ...e,
          id: e.id,
          _version: Date.now(),
          extendedProps: {
            ...e.extendedProps,
            taskStatus: newStatus
          },
          classNames: newStatus === 'done' && e.extendedProps.eventType === 'assignment' 
            ? ['completed-assignment'] 
            : []
        };
      }
      return e;
    });

    // 2. Optimistic Update (UI + Cache)
    setEvents(updatedEvents);
    saveCachedEvents(updatedEvents, fetchedRanges);

    try {
      const token = await getToken();
      await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tasks/${taskId}`,
        {
          method: 'PATCH',
          keepalive: true, // <--- THIS IS THE FIX
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        }
      );
      
      // Note: We don't check response.ok here strictly because keepalive 
      // requests might not return the response to the page if it unloads.
      // But they will reach the server.

    } catch (error) {
      console.error('Error toggling task status:', error);
      
      // Only revert if the page is still active/online
      // This prevents reverting just because the page is reloading
      if (navigator.onLine) {
        setEvents(originalEvents);
        saveCachedEvents(originalEvents, fetchedRanges);
        alert("Failed to update task status.");
      }
    }
  };

  // Format time for display
  const formatEventTime = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date and day of week
  const formatEventDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' });
    const month = d.toLocaleDateString('en-US', { month: 'long' });
    const day = d.getDate();
    const ordinal = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    return `${dayOfWeek}, ${month} ${day}${ordinal(day)}`;
  };

  // Mobile header title - context-aware based on current view
  const getMobileTitle = () => {
    if (!currentDate) return '';

    switch (currentView) {
      case 'timeGridDay':
        return currentDate.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      case 'timeGridWeek':
      case 'dayGridMonth':
      case 'listWeek':
      default:
        return currentDate.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric'
        });
    }
  };

  // Context-aware navigation for mobile - Previous
  const handleMobilePrev = () => {
    setMobileViewDropdownOpen(false);
    if (!calendarRef.current) return;
    const calendarApi = calendarRef.current.getApi();

    switch (currentView) {
      case 'timeGridDay': {
        const prevDay = new Date(calendarApi.getDate());
        prevDay.setDate(prevDay.getDate() - 1);
        calendarApi.gotoDate(prevDay);
        break;
      }
      case 'timeGridWeek':
      case 'listWeek': {
        const prevWeek = new Date(calendarApi.getDate());
        prevWeek.setDate(prevWeek.getDate() - 7);
        calendarApi.gotoDate(prevWeek);
        break;
      }
      case 'dayGridMonth':
        calendarApi.prev();
        break;
      default:
        calendarApi.prev();
    }
  };

  // Context-aware navigation for mobile - Next
  const handleMobileNext = () => {
    setMobileViewDropdownOpen(false);
    if (!calendarRef.current) return;
    const calendarApi = calendarRef.current.getApi();

    switch (currentView) {
      case 'timeGridDay': {
        const nextDay = new Date(calendarApi.getDate());
        nextDay.setDate(nextDay.getDate() + 1);
        calendarApi.gotoDate(nextDay);
        break;
      }
      case 'timeGridWeek':
      case 'listWeek': {
        const nextWeek = new Date(calendarApi.getDate());
        nextWeek.setDate(nextWeek.getDate() + 7);
        calendarApi.gotoDate(nextWeek);
        break;
      }
      case 'dayGridMonth':
        calendarApi.next();
        break;
      default:
        calendarApi.next();
    }
  };

  return (
    <div className={s.oceanCalendarWrapper}>
      <div
        ref={calendarContainerRef}
        className={`${s.calendarContainer} ${loading ? s.isLoading : ''}`}
        data-tour="calendar-main"
      >
        
        {/* Calendar sync - Hide on mobile */}
        {!isMobile && (
          <div className={`${s.syncToolbarElement} ${isChatbotExpanded ? s.chatbotOpen : s.chatbotClosed}`} data-tour="calendar-sync">
            <CalendarSync
              onSyncComplete={() => {
                // Clear cache and refetch when sync completes
                localStorage.removeItem(CACHE_KEY);
                localStorage.removeItem(CACHE_RANGES_KEY);
                localStorage.removeItem(CACHE_TIMESTAMP_KEY);
                localStorage.removeItem(CACHE_LAST_SYNC_KEY);
                setFetchedRanges([]);
                setLoading(true);
              }}
            />
          </div>
        )}

        {/* Inline toolbar elements - ONLY show on Desktop when chatbot is collapsed */}
        {!isChatbotExpanded && !isMobile && (
          <div className={s.inlineToolbarElements}>
            <button
              className={s.chatbotToggle}
              onClick={() => {
                if (isManuallyCollapsed) {
                  toggleSidebar(); // Expand sidebar first if collapsed
                }
                openChatbot(); // Then open chatbot
              }}
              title="Open Ocean Chat"
              data-tour="chatbot-toggle"
            >
              <img src={clearLogo} alt="Ocean" />
            </button>
          </div>
        )}

        {/* Shimmer overlay while loading */}
        {loading && (
          <div className={s.shimmerOverlay}>
            <div className={s.shimmer}></div>
          </div>
        )}

        {/* Mobile Custom Header - Only show on mobile */}
        {isMobile && (
          <div className={s.mobileCalendarHeader}>
            {/* Hamburger Menu */}
            <div className={s.mobileHeaderLeft}>
              <button
                className={s.hamburgerButton}
                onClick={() => setMobileViewDropdownOpen(!mobileViewDropdownOpen)}
                aria-label="View options"
              >
                <HamburgerIcon />
              </button>

              {/* Dropdown Menu */}
              {mobileViewDropdownOpen && (
                <div
                  ref={mobileViewDropdownRef}
                  className={s.mobileViewDropdown}
                >
                  <button
                    className={`${s.mobileViewOption} ${currentView === 'timeGridDay' ? s.active : ''}`}
                    onClick={() => {
                      if (!calendarRef.current) return;
                      calendarRef.current.getApi().changeView('timeGridDay');
                      setMobileViewDropdownOpen(false);
                    }}
                  >
                    Day
                  </button>
                  <button
                    className={`${s.mobileViewOption} ${currentView === 'timeGridWeek' ? s.active : ''}`}
                    onClick={() => {
                      if (!calendarRef.current) return;
                      calendarRef.current.getApi().changeView('timeGridWeek');
                      setMobileViewDropdownOpen(false);
                    }}
                  >
                    Week
                  </button>
                  <button
                    className={`${s.mobileViewOption} ${currentView === 'dayGridMonth' ? s.active : ''}`}
                    onClick={() => {
                      if (!calendarRef.current) return;
                      calendarRef.current.getApi().changeView('dayGridMonth');
                      setMobileViewDropdownOpen(false);
                    }}
                  >
                    Month
                  </button>
                  <button
                    className={`${s.mobileViewOption} ${currentView === 'listWeek' ? s.active : ''}`}
                    onClick={() => {
                      if (!calendarRef.current) return;
                      calendarRef.current.getApi().changeView('listWeek');
                      setMobileViewDropdownOpen(false);
                    }}
                  >
                    List
                  </button>

                  {/* Separator */}
                  <div className={s.dropdownSeparator}></div>

                  {/* Sync button */}
                  <button
                    className={`${s.mobileViewOption} ${s.syncOption}`}
                    onClick={handleSyncClick}
                  >
                    <SyncIcon className={s.optionIcon} />
                    <span>Sync</span>
                  </button>

                  {/* Google sync */}
                  <button
                    className={`${s.mobileViewOption} ${s.syncOption} ${
                      providerStatus.google?.syncing ? s.connected : ''
                    }`}
                    onClick={() => handleProviderClick('google')}
                    disabled={isSyncingAction}
                  >
                    <GoogleIcon className={s.optionIcon} />
                    <span>Google</span>
                    {providerStatus.google?.syncing && (
                      <span className={s.statusIndicator}>●</span>
                    )}
                  </button>

                  {/* Outlook sync */}
                  <button
                    className={`${s.mobileViewOption} ${s.syncOption} ${
                      providerStatus.outlook?.syncing ? s.connected : ''
                    }`}
                    onClick={() => handleProviderClick('outlook')}
                    disabled={isSyncingAction}
                  >
                    <OutlookIcon className={s.optionIcon} />
                    <span>Outlook</span>
                    {providerStatus.outlook?.syncing && (
                      <span className={s.statusIndicator}>●</span>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Month/Year Title */}
            <div className={s.mobileHeaderCenter}>
              <h1 className={s.mobileTitle}>{getMobileTitle()}</h1>
            </div>

            {/* Navigation Arrows and Chatbot */}
            <div className={s.mobileHeaderRight}>
              <button
                className={s.navArrowButton}
                onClick={handleMobilePrev}
                aria-label="Previous"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                className={s.navArrowButton}
                onClick={handleMobileNext}
                aria-label="Next"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                className={s.chatbotButton}
                onClick={openChatbot}
                aria-label="Open Ocean Chat"
              >
                <img src={clearLogo} alt="Ocean" className={s.oceanLogo} />
              </button>
            </div>
          </div>
        )}

        {/* Timezone picker — week/day views only. List view uses a DOM portal below.
            Hidden on month view because there's no good spot to anchor it. */}
        {currentView !== 'listWeek' && currentView !== 'dayGridMonth' && (
          <div className={s.timezonePicker} ref={timezoneDropdownRef}>
            <button
              className={s.timezoneButton}
              onClick={() => setShowTimezoneDropdown(!showTimezoneDropdown)}
            >
              TZ {getTimezoneAbbr(selectedTimezone)}
            </button>
            {showTimezoneDropdown && (
              <div className={s.timezoneDropdown}>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <button
                    key={tz.value}
                    className={`${s.timezoneOption} ${selectedTimezone === tz.value ? s.selected : ''}`}
                    onClick={() => handleTimezoneChange(tz.value)}
                  >
                    {tz.label} ({tz.abbr})
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* List view: TZ button portalled into every day cushion so the
            currently-sticky day header always shows it. */}
        {currentView === 'listWeek' && tzPortalContainers.map((target, i) =>
          createPortal(
            <button
              className={s.timezoneButton}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setTzDropdownFixedPos({ top: rect.bottom + 4, left: rect.left });
                setShowTimezoneDropdown(prev => !prev);
              }}
            >
              {getTimezoneAbbr(selectedTimezone)}
            </button>,
            target,
            `tz-portal-${i}`
          )
        )}

        {/* List view dropdown — rendered into body at fixed coords to avoid cushion overflow clipping */}
        {currentView === 'listWeek' && showTimezoneDropdown && createPortal(
          <div
            ref={tzDropdownPortalRef}
            className={s.timezoneDropdown}
            style={{ position: 'fixed', top: tzDropdownFixedPos.top, left: tzDropdownFixedPos.left, zIndex: 9999 }}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <button
                key={tz.value}
                className={`${s.timezoneOption} ${selectedTimezone === tz.value ? s.selected : ''}`}
                onClick={() => handleTimezoneChange(tz.value)}
              >
                {tz.label} ({tz.abbr})
              </button>
            ))}
          </div>,
          document.body
        )}

        <div data-tour="view-switcher" style={{ display: 'contents' }}>
          <FullCalendar
            key={`${selectedTimezone}-${isMobile ? 'mobile' : 'desktop'}`}  // Force re-render when timezone or screen size changes
            ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            listPlugin,
            momentTimezonePlugin
          ]}

          // View settings
          initialView="timeGridWeek"
          timeZone={selectedTimezone}
          headerToolbar={isMobile ? false : {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
          }}

          // Time settings
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="01:00:00"
          snapDuration="00:15:00"  // Drag/resize in 15-minute increments
          scrollTime="08:00:00"  // Scroll to 8am on load, but full 24hrs visible
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: true,
            meridiem: 'short',
            hour12: true
          }}

          // Event settings
          events={[...events, ...suggestedEvents]}  // Merge regular and suggested events
          eventClassNames={(eventInfo) => {
            const props = eventInfo.event.extendedProps;
            const classes = [];

            // Short event styling (less than 30 minutes)
            const start = eventInfo.event.start;
            const end = eventInfo.event.end;
            if (start && end) {
              const durationMinutes = (end - start) / (1000 * 60);
              if (durationMinutes < 30) {
                classes.push('short-event');
              }
            }

            // Suggested event styling
            if (props?.source === 'suggestion') {
              classes.push('suggested-event');
            }

            // Completed assignment styling
            const isCompleted = props?.taskStatus === 'done';
            const isAssignment = props?.eventType === 'assignment';
            if (isCompleted && isAssignment) {
              classes.push('completed-assignment');
            }

            return classes.join(' ');
          }}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          datesSet={(dateInfo) => {
            handleDatesSet(dateInfo);
            handleViewChange(dateInfo);
          }}  // Detect when user navigates to new date range
          dateClick={handleDateClick}  // Single tap/click to create event
          eventOrder="-allDay,start" // All-day events first, then sort by start time

          // Drag and drop / resize settings
          editable={true}

          // Selection settings
          selectable={true}
          selectMirror={true}
          selectMinDistance={0}
          selectLongPressDelay={150}  // Shorter delay for touch selection on mobile
          longPressDelay={150}  // Shorter delay for drag on mobile
          defaultTimedEventDuration="01:00:00"
          select={handleSelect}

          // Display settings
          height="100%"
          expandRows={true}
          nowIndicator={true}
          dayMaxEvents={3} // Show max 3 items (2 events + "+x more")
          dayMaxEventRows={3} // Limit to 3 rows per day
          moreLinkClick="popover" // Show popover when clicking "+x more"
          weekends={true}
          allDayText=" " // Hidden - using custom timezone picker overlay

          // View-specific settings
          views={{
            timeGridWeek: {
              // OPTIONAL JS FIX: "narrow" gives single letters (S, M, T...)
              // This guarantees NO OVERFLOW on small screens
              dayHeaderFormat: isMobile
                ? { weekday: 'narrow', day: 'numeric' } // "S 12"
                : { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true } // "Sun 12/7"
            }
          }}

          // Styling
          eventDisplay="block"

          // Custom event rendering
          eventContent={(eventInfo) => {
            return <EventContent
              eventInfo={eventInfo}
              onTaskToggle={handleTaskStatusToggle}
            />;
          }}
        />
        </div>
      </div>

      {/* Event Details Popover */}
      {showEventModal && selectedEvent && (
        <div
          ref={modalRef}
          className={s.eventPopover}
          style={{
            top: `${modalPosition.top}px`,
            left: `${modalPosition.left}px`,
            borderColor: selectedEvent.color
          }}
        >
          <div className={s.modalHeader}>
            <h2>{isEditing ? 'Edit Event' : selectedEvent.title}</h2>
            <div className={s.headerActions}>
              {!isEditing && !selectedEvent.isSuggestion && (
                <>
                  <button
                    className={s.iconBtn}
                    onClick={handleEdit}
                    title="Edit event"
                  >
                    <PencilIcon className={s.actionIcon} />
                  </button>
                  <button
                    className={`${s.iconBtn} ${s.deleteBtn}`}
                    onClick={handleDelete}
                    title="Delete event"
                  >
                    <TrashIcon className={s.actionIcon} />
                  </button>
                </>
              )}
              <button className={s.closeBtn} onClick={closeEventModal}>×</button>
            </div>
          </div>

          {selectedEvent.isSuggestion && (
            <div className={s.suggestionBadge}>AI Suggested</div>
          )}

          {!isEditing ? (
            <>
              <div className={s.modalDateTime}>
                <span className={s.dateText}>
                  {formatEventDate(selectedEvent.start)} · {formatEventTime(selectedEvent.start)} - {formatEventTime(selectedEvent.end)}
                </span>
              </div>

              <div className={s.modalDivider}></div>

              <div className={s.modalBody}>
                {selectedEvent.location && (
                  <div className={s.locationRow}>
                    <LocationIcon className={s.locationIconModal} />
                    <span>{selectedEvent.location}</span>
                  </div>
                )}

                {selectedEvent.description && (
                  <div className={s.description}>
                    {selectedEvent.description}
                  </div>
                )}

                {selectedEvent.isSuggestion && (selectedEvent.aiReasoning || selectedEvent.userMovedTime) && (
                  <div className={s.aiReasoning}>
                    {selectedEvent.userMovedTime ? (
                      <span className={s.userSelectedTime}>
                        <InfoIcon className={s.infoIcon} />
                        User selected time
                      </span>
                    ) : (
                      <>
                        <span className={s.reasoningLabel}>Why this time?</span>
                        <p>{selectedEvent.aiReasoning}</p>
                      </>
                    )}
                  </div>
                )}

                {selectedEvent.isSuggestion && (
                  <div className={s.suggestionActions}>
                    <button
                      className={s.dismissBtn}
                      onClick={() => handleDismissSuggestion(selectedEvent.suggestionId)}
                    >
                      Dismiss
                    </button>
                    <button
                      className={s.acceptBtn}
                      onClick={() => handleAcceptSuggestion(selectedEvent.suggestionId)}
                    >
                      Add to Calendar
                    </button>
                  </div>
                )}

                {(selectedEvent.calendarType === 'google' || selectedEvent.calendarType === 'outlook') && (
                  <div className={s.importSourceRow}>
                    <InfoIcon className={s.infoIcon} />
                    <span className={s.importSourceText}>
                      Imported from {selectedEvent.calendarType === 'google' ? 'Google' : 'Outlook'} Calendar
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={s.editForm}>
              <div className={s.formGroup}>
                <label>Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Event title"
                />
              </div>

              <div className={s.formGroup}>
                <label>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Event description"
                  rows={3}
                />
              </div>

              <div className={s.formGroup}>
                <label>Location</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="Event location"
                />
              </div>

              {/* Time selection - different for assignments vs other events */}
              {selectedEvent?.type === 'assignment' ? (
                // Assignment: Single due date picker
                <div className={s.formGroup}>
                  <label>Due Date</label>
                  <DatePicker
                    selected={editForm.end}
                    onChange={(date) => setEditForm({
                      ...editForm,
                      start: date,  // Keep both in sync
                      end: date
                    })}
                    showTimeSelect
                    timeFormat="h:mm aa"
                    timeIntervals={15}
                    dateFormat="MMM d, yyyy h:mm aa"
                    className={s.datePicker}
                    calendarClassName={s.datePickerCalendar}
                  />
                </div>
              ) : (
                // Non-assignment: Start and end time pickers
                <div className={s.formRow}>
                  <div className={s.formGroup}>
                    <label>Start</label>
                    <DatePicker
                      selected={editForm.start}
                      onChange={(date) => setEditForm({ ...editForm, start: date })}
                      showTimeSelect
                      timeFormat="h:mm aa"
                      timeIntervals={15}
                      dateFormat="MMM d, yyyy h:mm aa"
                      className={s.datePicker}
                      calendarClassName={s.datePickerCalendar}
                    />
                  </div>
                  <div className={s.formGroup}>
                    <label>End</label>
                    <DatePicker
                      selected={editForm.end}
                      onChange={(date) => setEditForm({ ...editForm, end: date })}
                      showTimeSelect
                      timeFormat="h:mm aa"
                      timeIntervals={15}
                      dateFormat="MMM d, yyyy h:mm aa"
                      className={s.datePicker}
                      calendarClassName={s.datePickerCalendar}
                      minDate={editForm.start}
                    />
                  </div>
                </div>
              )}

              <div className={s.formActions}>
                <button
                  className={s.cancelBtn}
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className={s.saveBtn}
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <div
          ref={createModalRef}
          className={s.eventPopover}
          style={{
            top: `${modalPosition.top}px`,
            left: `${modalPosition.left}px`,
            borderColor: '#3B82F6'
          }}
        >
          <div className={s.modalHeader}>
            <h2>New Event</h2>
            <button className={s.closeBtn} onClick={closeCreateModal}>×</button>
          </div>

          <div className={s.editForm}>
            <div className={s.formGroup}>
              <label>Title</label>
              <input
                type="text"
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                placeholder="Event title"
                autoFocus
              />
            </div>

            <div className={s.formGroup}>
              <label>Course</label>
              <select
                value={createForm.courseId || ''}
                onChange={(e) => setCreateForm({ ...createForm, courseId: e.target.value || null })}
                className={s.courseSelect}
              >
                <option value="">No course (Personal)</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.course_name}
                  </option>
                ))}
              </select>
            </div>

            <div className={s.formGroup}>
              <label>Description</label>
              <textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Event description"
                rows={3}
              />
            </div>

            <div className={s.formGroup}>
              <label>Location</label>
              <input
                type="text"
                value={createForm.location}
                onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                placeholder="Event location"
              />
            </div>

            <div className={s.formRow}>
              <div className={s.formGroup}>
                <label>Start</label>
                <DatePicker
                  selected={createForm.start}
                  onChange={(date) => setCreateForm({ ...createForm, start: date })}
                  showTimeSelect
                  timeFormat="h:mm aa"
                  timeIntervals={15}
                  dateFormat="MMM d, yyyy h:mm aa"
                  className={s.datePicker}
                />
              </div>
              <div className={s.formGroup}>
                <label>End</label>
                <DatePicker
                  selected={createForm.end}
                  onChange={(date) => setCreateForm({ ...createForm, end: date })}
                  showTimeSelect
                  timeFormat="h:mm aa"
                  timeIntervals={15}
                  dateFormat="MMM d, yyyy h:mm aa"
                  className={s.datePicker}
                  minDate={createForm.start}
                />
              </div>
            </div>

            <div className={s.formActions}>
              <button
                className={s.cancelBtn}
                onClick={closeCreateModal}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                className={s.saveBtn}
                onClick={handleCreateEvent}
                disabled={isCreating}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo Delete Toast */}
      {showUndoToast && deletedEvent && (
        <div className={s.undoToast}>
          <span>"{deletedEvent.title}" deleted</span>
          <button onClick={handleUndoDelete} className={s.undoButton}>
            Undo
          </button>
        </div>
      )}
    </div>
  );
};

// Custom event content component
const EventContent = ({ eventInfo, onTaskToggle }) => {
  const props = eventInfo.event.extendedProps;
  const isMonthView = eventInfo.view.type === 'dayGridMonth';
  const isAssignment = props.eventType === 'assignment';
  const isCompleted = props.taskStatus === 'done';

  // Handle checkbox click
  const handleCheckboxClick = (e) => {
    e.stopPropagation(); // Prevent event modal from opening
    e.preventDefault();
    if (isAssignment && props.taskId && onTaskToggle) {
      onTaskToggle(props.taskId, props.taskStatus);
    }
  };

  // Month view: no checkbox
  if (isMonthView) {
    return (
      <div className={`${s.customEventContent} ${s.monthView}`}>
        <span className={s.eventTime}>{eventInfo.timeText}</span>
        <span className={s.eventCourse}>{props.courseName}</span>
      </div>
    );
  }

  // Calculate event duration in minutes
  const start = eventInfo.event.start;
  const end = eventInfo.event.end;
  const durationMinutes = end ? (end - start) / (1000 * 60) : 0;

  // Format due time for assignments with full AM/PM
  const getDueTime = () => {
    if (!end) return '';

    return end.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Determine what to show based on event height/duration
  // Each line needs ~20px, title needs ~20px, padding ~8px
  // 30 min slot = 30px, 60 min = 60px, etc.
  const showTime = isAssignment || durationMinutes >= 45; // Always show time for assignments
  const showLocation = durationMinutes >= 75 && props.location; // Need ~60px
  const showGrade = durationMinutes >= 105 && props.gradePercentage; // Need ~80px

  // Week/Day view: show checkbox for assignments
  return (
    <div className={`${s.customEventContent} ${isCompleted ? s.completedTask : ''}`}>
      {isAssignment && (
        <button
          className={`${s.taskCheckbox} ${isCompleted ? s.checked : ''}`}
          onClick={handleCheckboxClick}
          aria-label={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
          aria-checked={isCompleted}
          role="checkbox"
          tabIndex={0}
        >
          {isCompleted && (
            <svg viewBox="0 0 16 16" className={s.checkIcon}>
              <path
                d="M13.5 3L6 10.5L2.5 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      )}
      <div className={s.eventDetails}>
        <div className={s.eventTitle}>{eventInfo.event.title}</div>
        {showTime && (
          <div className={s.eventTime}>
            {isAssignment ? `Due at ${getDueTime()}` : eventInfo.timeText}
          </div>
        )}
        {showLocation && (
          <div className={s.eventLocation}>
            <LocationWhiteIcon className={s.locationIcon} />
            {props.location}
          </div>
        )}
        {showGrade && (
          <div className={s.eventGrade}>{props.gradePercentage}% of grade</div>
        )}
      </div>
    </div>
  );
};

export default OceanCalendar;